import { message } from "antd";
import { ENV } from "../../../config";
import { AUTHENTICATE_VIEW_CONSTANT } from "../../../shared/utils/CommonConstants";
import { SESSION_CONSTANT } from "../../../shared/utils/SessionConstants";
import { loadSsoFeatures, SsoLoadResult } from "./SsoFeatureService";

export type SsoRefreshResult = SsoLoadResult | "no-session";

const runSsoFeatureLoad = async (): Promise<SsoRefreshResult> => {
  const { msalInstance, API_SCOPE } = await import("../../../core/services/msalInstance");
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    return "no-session";
  }

  let accessToken: string;
  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({ scopes: [API_SCOPE], account });
    accessToken = tokenResponse.accessToken;
    localStorage.setItem(SESSION_CONSTANT.TOKEN_KEY, accessToken);
  } catch {
    message.error(AUTHENTICATE_VIEW_CONSTANT.SSO_TOKEN_REFRESH_FAILED_MESSAGE);
    return "failed";
  }

  try {
    const response = await fetch(`${ENV.API_URL}/api/auth/sso-session`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
    });
    if (response.status === 401) {
      return "unmatched";
    }
    if (!response.ok) {
      return "failed";
    }
    return await loadSsoFeatures(response);
  } catch {
    message.error(AUTHENTICATE_VIEW_CONSTANT.SSO_SESSION_REFRESH_FAILED_MESSAGE);
    return "failed";
  }
};

export const refreshSsoFeatures = async (): Promise<SsoRefreshResult> => runSsoFeatureLoad();

const hasStoredFeatures = (): boolean => {
  try {
    const parsed = JSON.parse(localStorage.getItem(SESSION_CONSTANT.FEATURES_KEY) ?? "[]");
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
};

let startupResult: SsoRefreshResult | null = null;
let startupAttempted = false;
export const getStartupSsoResult = (): SsoRefreshResult | null => startupResult;

export const restoreCachedSsoFeatures = async (): Promise<SsoRefreshResult | null> => {
  if (startupAttempted || hasStoredFeatures()) {
    return null;
  }
  startupAttempted = true;
  startupResult = await runSsoFeatureLoad();
  return startupResult;
};

export const resetStartupSsoRestoreForTests = (): void => {
  startupAttempted = false;
  startupResult = null;
};
